import { LinearProgress } from "@mui/material"
import Stack from "@mui/material/Stack"
import { FC, useEffect, useState } from "react"

import { useAccount } from "../../../../states/account"
import { FilterSortSelection } from "src/components/core/filterSortSelector/FilterSortSelector"
import { create } from "src/helpers/bem"

import { formatPrice } from "src/helpers/price"
import { checkNullOrEmpty } from "src/helpers/string"

import { fetchPurchasedServices } from "src/queriesXRM/purchaseorder"
import { useBooking } from "src/states/bookingDetail"
import { useBookings } from "src/states/bookings"
import { useLocale } from "src/states/locale"
import { SharedPageProps } from "src/types/SharedPageProps"

import {
  xRMWebshopOrderedCategories,
  xRMWebshopOrdersByDebitor,
} from "src/types/xRM"

import { BookingOverview } from "./BookingOverview"
import styles from "./OrderedServices.module.scss"
import { Orders } from "./Orders"

const bem = create(styles, "OrderedServices")

interface DebitorOption {
  id: string
  label: string
}

export const OrderedServices: FC<SharedPageProps> = (props) => {
  const { messages } = props
  const [loading, setLoading] = useState(false)
  const [activeDebitor, setActiveDebitor] = useState("")
  const [webshopCategories, setWebshopCategories] = useState<
    xRMWebshopOrderedCategories[]
  >([])
  const [webshopOrdersByDebitor, setWebshopOrdersByDebitor] = useState<
    xRMWebshopOrdersByDebitor[]
  >([])
  const [debitorOptions, setDebitorOptions] = useState<DebitorOption[]>([])
  const locale = useLocale()
  const { loadingSalesorderDeletion, booking } = useBooking()
  const { bookings } = useBookings()
  const { contact: { debitorId, id: contactId } = {} } = useAccount()

  const activeBooking = bookings.find(
    (actBooking) => actBooking.id === booking?.id,
  )
  const showTotalSum =
    debitorId === activeDebitor ||
    contactId === activeBooking?.senderContactId ||
    contactId === activeBooking?.contactId
  const totalSum = webshopCategories.reduce(
    (acc, category) => acc + (category?.totalAmount ?? 0),
    0,
  )

  const loadData = async () => {
    if (booking?.id) {
      setActiveDebitor("")
      const orders = await fetchPurchasedServices(booking.id)
      setWebshopOrdersByDebitor(orders)
      setActiveDebitor(orders[0]?.debitorId ?? "")
      setDebitorOptions(
        orders.map((order: xRMWebshopOrdersByDebitor) => ({
          id: order.debitorId,
          label: order.debitorName,
        })),
      )
    }
    setLoading(false)
  }

  useEffect(() => {
    var selectedDebitor = webshopOrdersByDebitor.filter(
      (order: xRMWebshopOrdersByDebitor) => order.debitorId === activeDebitor,
    )
    setWebshopCategories(selectedDebitor[0]?.categories ?? [])
  }, [activeDebitor])

  useEffect(() => {
    if (booking) {
      setLoading(true)
      loadData()
    }
  }, [booking?.id, locale])

  if (loading || loadingSalesorderDeletion) {
    return (
      <div className={bem()}>
        <div className={bem("loading")}>
          <LinearProgress color="primary" />
        </div>
      </div>
    )
  }

  if (!booking) {
    return (
      <div className={bem()}>
        <div className={bem("no-data")}>
          <h3>Es sind keine gebuchten Serviceleistungen vorhanden.</h3>
        </div>
      </div>
    )
  }

  return (
    <div className={bem()}>
      <Stack
        className={bem("headRow")}
        direction={"row"}
        justifyContent="space-between"
      >
        <Stack direction={"column"}>
          <div className={bem("header")}>
            {messages.pages.orderedServices.header.service}
          </div>
          <div className={bem("subheader")}>
            {`${messages.pages.orderedServices.header.order} ${activeBooking?.name}`}
          </div>
        </Stack>
        <Stack direction={"row"} spacing={1.25}>
          <div className={bem("chip")}>
            <h3>{checkNullOrEmpty(activeBooking?.standhall) ?? "-"}</h3>
            <p>{messages.pages.orderedServices.header.hall}</p>
          </div>
          <div className={bem("chip")}>
            <h3>{checkNullOrEmpty(activeBooking?.standnumber) ?? "-"}</h3>
            <p>{messages.pages.orderedServices.header.stand}</p>
          </div>
          {showTotalSum && (
            <div className={bem("chip", "total")}>
              <h3>{`${formatPrice(totalSum) ?? "- €"}`}</h3>
              <p>{messages.pages.orderedServices.header.total}</p>
            </div>
          )}
        </Stack>
      </Stack>
      <FilterSortSelection
        disabled={debitorOptions.length <= 1}
        options={debitorOptions}
        sizeVariant="large"
        value={activeDebitor}
        onChange={setActiveDebitor}
      />
      <BookingOverview bookingId={booking?.id!} {...props} />
      <Orders
        activeDebitorId={activeDebitor}
        data={webshopCategories}
        {...props}
      />
    </div>
  )
}
